import Vue from 'vue'
import { Storage } from '@capacitor/storage'
import { onLogout, hasApolloToken, hasRefreshToken, REFRESH_TOKEN, refreshTokenSet, AUTH_TOKEN } from '@/vue-apollo'

import { RefreshTokenGQL } from '@/graphql/mutations'

const state = Vue.observable({
  refreshingToken: false
})

export const refreshingToken = {
  computed: {
    refreshingToken () {
      return state.refreshingToken
    }
  }
}

export const errorCodes = {
  computed: {
    errorCodes () {
      return {
        userEmailExists: this.$i18n.t('errors.codes.userEmailExists'),
        Unauthorized: this.$i18n.t('errors.codes.unauthorized'),
        Forbidden: this.$i18n.t('errors.codes.forbidden'),
        userNotFound: this.$i18n.t('errors.codes.userNotFound'),
        partnerManagerAlreadyHasPartner: this.$i18n.t('errors.codes.partnerManagerAlreadyHasPartner'),
        internalServerError: this.$i18n.t('errors.codes.internalServerError'),
        shopIsNotActive: this.$i18n.t('errors.code.shopIsNotActive'),
        terminalIsNotActive: this.$i18n.t('errors.code.terminalIsNotActive'),
        terminalEditOnlyREADY: this.$i18n.t('errors.code.terminalEditOnlyREADY'),
        terminalAlreadyExisted: this.$i18n.t('errors.code.terminalAlreadyExisted'),
        cannotSendConfirmCode: this.$i18n.t('errors.code.cannotSendConfirmCode'),
        invalidConfirmCode: this.$i18n.t('errors.code.invalidConfirmCode'),
        terminalMustBeFilled: this.$i18n.t('errors.code.terminalMustBeFilled'),
        managerAlreadyExist: this.$i18n.t('errors.code.managerAlreadyExist'),
        min6max200: this.$i18n.t('errors.code.min6max200'),
        min6: this.$i18n.t('errors.code.min6'),
        max200: this.$i18n.t('errors.code.max200'),
        phone: this.$i18n.t('errors.code.phone'),
        Unknown: this.$i18n.t('errors.code.Unknown'),
        disposableUuidNotFound: this.$i18n.t('errors.code.disposableUuidNotFound'),
        latinLettersNumbersDash: this.$i18n.t('errors.code.latinLettersNumbersDash'),
        latinLettersNumber: this.$i18n.t('errors.code.latinLettersNumber'),
        canNotEditYourAccess: this.$i18n.t('errors.code.canNotEditYourAccess'),
        needRechargeBalance: this.$i18n.t('errors.code.needRechargeBalance'),
        invalidPhoneNumber: this.$i18n.t('errors.code.invalidPhoneNumber'),
        userLoginExists: this.$i18n.t('errors.code.userLoginExists'),
        wrongDomainFormat: this.$i18n.t('errors.codes.wrongDomainFormat'),
        invalidPassword: this.$i18n.t('errors.codes.invalidPassword'),
        equalPasswords: this.$i18n.t('errors.codes.equalPasswords'),
        invalidToken: this.$i18n.t('errors.codes.invalidToken'),
        partnerManagerNotFound: this.$i18n.t('errors.codes.partnerManagerNotFound'),
        invalidLogin: this.$i18n.t('errors.codes.invalidLogin'),
        invalidCode: this.$i18n.t('errors.codes.invalidCode'),
        confirmTryLimit: this.$i18n.t('errors.codes.confirmTryLimit'),
        lessThan8: this.$i18n.t('errors.codes.lessThan8'),
        networkError: this.$i18n.t('errors.networkError')
      }
    }
  }
}

export const notifyGraphqlError = {
  mixins: [errorCodes],

  methods: {
    notifyGraphqlError (e) {
      const errors = e.graphQLErrors

      for (const err of errors) {
        let code = err.message
        if (code === 'error1C') {
          continue
        }

        if (err.extensions) {
          code = err.extensions.message ? err.extensions.message : err.message
        }

        if (err.extensions && err.extensions.status === 403) {
          code = 'Forbidden'
        }

        if (err.extensions && err.extensions.errors && err.extensions.errors.length > 0) {
          const codes = err.extensions.errors

          for (const c of codes) {
            if (c.defaultMessage) {
              this.$notify({
                type: 'error',
                message: this.errorCodes[c.defaultMessage] ? this.errorCodes[c.defaultMessage] : c.defaultMessage,
                delay: 150,
                timeout: 3000
              })
            }
          }

          continue
        }
        this.$notify({
          type: 'error',
          message: this.errorCodes[code] ? this.errorCodes[code] : code,
          delay: 150,
          timeout: 3000
        })
      }
    }
  }
}

export const refreshExec = {
  mixins: [errorCodes],

  methods: {
    async logoutExec () {
      try {
        await Promise.all([
          this.$router.push({ name: 'auth_login' }),
          onLogout(this.$apollo.getClient())
        ])
      } catch (e) {}
    },
    async refreshExec () {
      const [accessTokenVal, refreshTokenVal] = await Promise.all([
        await Storage.get({ key: AUTH_TOKEN }).then(res => res.value),
        await Storage.get({ key: REFRESH_TOKEN }).then(res => res.value)
      ])
      if (refreshTokenVal && accessTokenVal && !state.refreshingToken) {
        state.refreshingToken = true
        try {
          const { accessToken, refreshToken } = await this.$apollo.mutate({
            mutation: RefreshTokenGQL,
            variables: {
              credentials: {
                accessToken: accessTokenVal,
                refreshToken: refreshTokenVal
              }
            }
          }).then(res => res?.data?.refreshToken ?? { accessToken: null, refreshToken: null })

          if (!accessToken && !refreshToken) {
            state.refreshingToken = false
            this.$notify({
              type: 'error',
              message: this.errorCodes[this.errorCodes.Unauthorized],
              delay: 150,
              timeout: 3000
            })
            await this.logoutExec()
            return
          }

          await refreshTokenSet(this.$apollo.getClient(), accessToken, refreshToken)
          try {
            await this.$apollo.getClient().reFetchObservableQueries(true)
          } catch (e) {
            console.log(e)
          }
          state.refreshingToken = false
        } catch (e) {
          state.refreshingToken = false
          this.$notify({
            type: 'error',
            message: this.errorCodes.Unauthorized,
            delay: 150,
            timeout: 3000
          })
          await this.logoutExec()
        }
      }
    }
  }
}

export const refreshTokenMutate = {
  methods: {
    async logoutExec () {
      try {
        await Promise.all([
          this.$router.push({ name: 'auth_login' }),
          onLogout(this.$apollo.getClient())
        ])
      } catch (e) {}
    },
    async refreshTokenMutate () {
      const [accessTokenVal, refreshTokenVal] = await Promise.all([
        await Storage.get({ key: AUTH_TOKEN }).then(res => res.value),
        await Storage.get({ key: REFRESH_TOKEN }).then(res => res.value)
      ])
      if (refreshTokenVal && accessTokenVal && !state.refreshingToken) {
        try {
          const { accessToken, refreshToken } = await this.$apollo.mutate({
            mutation: RefreshTokenGQL,
            variables: {
              credentials: {
                accessToken: accessTokenVal,
                refreshToken: refreshTokenVal
              }
            }
          }).then(res => res?.data?.refreshToken ?? { accessToken: null, refreshToken: null })

          if (!accessToken && !refreshToken) {
            this.$notify({
              type: 'error',
              message: this.errorCodes[this.errorCodes.Unauthorized],
              delay: 150,
              timeout: 3000
            })
            await this.logoutExec()
            return
          }

          await refreshTokenSet(this.$apollo.getClient(), accessToken, refreshToken)
        } catch (e) {
          this.$notify({
            type: 'error',
            message: this.errorCodes.Unauthorized,
            delay: 150,
            timeout: 3000
          })
          await this.logoutExec()
        }
      }
    }
  }
}

let tkInterval
export const refreshSession = {
  mixins: [refreshTokenMutate],

  methods: {
    refreshSession () {
      clearInterval(tkInterval)
      tkInterval = setInterval(async () => {
        if (!await hasRefreshToken()) {
          clearInterval(tkInterval)
        } else {
          await this.refreshTokenMutate()
        }
      }, 1000 * 60 * 4)
    }
  }
}

export default {
  mixins: [errorCodes, refreshExec],

  methods: {
    async logoutExec () {
      try {
        await Promise.all([
          this.$router.push({ name: 'auth_login' }),
          onLogout(this.$apollo.getClient())
        ])
      } catch (e) {}
    },

    async showError (e) {
      if (e.detail && e.detail.networkError && e.detail.networkError.message) {
        this.$notify({
          type: 'error',
          message: this.errorCodes.networkError,
          delay: 150,
          timeout: 3000
        })
      }

      const authorized = await hasApolloToken()
      if (!authorized) {
        try {
          await Promise.all([
            this.$router.push({ name: 'auth_login' }),
            onLogout(this.$apollo.getClient())
          ])
        } catch (e) {}
      }

      if (!e.detail.gqlError) {
        console.log(e.detail)
        return
      }

      const code = e.detail.gqlError.message

      if (code.match(/Token is expired|Token expired|token expired|token is expired|token contains an invalid number of segments|illegal base64 data at input byte 21|signature is invalid|Unauthorized|/g)) {
        await this.refreshExec()
        return
      }

      if (code === 'error1C') {
        return
      }
      this.$notify({
        type: 'error',
        message: this.errorCodes[code] ? this.errorCodes[code] : code,
        delay: 150,
        timeout: 3000
      })
    }
  },

  mounted () {
    window.addEventListener('graphqlError', this.showError)
  }
}

export const dispatchError = (e) => {
  const gqlErrorEvent = new CustomEvent('graphqlError', {
    detail: e
  })

  window.dispatchEvent(gqlErrorEvent)
}
